import type { PlacedObject } from '../store/types';

/** Rough volume of triangle mesh via divergence theorem (Y-up). */
export function meshVolumeMm3(positions: Float32Array, scale: [number, number, number]): number {
  let vol = 0;
  const [sx, sy, sz] = scale;
  for (let i = 0; i + 8 < positions.length; i += 9) {
    const ax = positions[i] * sx,
      ay = positions[i + 1] * sy,
      az = positions[i + 2] * sz;
    const bx = positions[i + 3] * sx,
      by = positions[i + 4] * sy,
      bz = positions[i + 5] * sz;
    const cx = positions[i + 6] * sx,
      cy = positions[i + 7] * sy,
      cz = positions[i + 8] * sz;
    vol += ax * (by * cz - bz * cy) - ay * (bx * cz - bz * cx) + az * (bx * cy - by * cx);
  }
  return Math.abs(vol) / 6;
}

export function meshHeightMm(positions: Float32Array, scale: [number, number, number]): number {
  let minY = Infinity,
    maxY = -Infinity;
  const sy = scale[1];
  for (let i = 1; i < positions.length; i += 3) {
    const y = positions[i] * sy;
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }
  if (!Number.isFinite(minY)) return 0;
  return Math.max(0, maxY - minY);
}

export interface Estimate {
  volumeCm3: number;
  filamentGrams: number;
  approxMinutes: number;
  note: string;
}

/**
 * Size-aware shell + sparse-infill heuristic, calibrated vs OrcaSlicer 2.4.2:
 * - 20 mm cube, 2 walls / 15% grid / 0.20 mm → Orca 3.73 g / 21 m (est ≈ 4.0 g / 20 m)
 * - 90×78×66 solid box, same profile → Orca 118 g / 104 m (est ≈ 125 g / 102 m)
 * Previous flat fillFactor over-estimated large solids (~232 g / 495 m).
 */
export function estimatePrint(
  objects: PlacedObject[],
  settings: Record<string, string | number | boolean>
): Estimate {
  let vol = 0;
  let height = 0;
  for (const o of objects) {
    vol += meshVolumeMm3(o.positions, o.scale);
    height = Math.max(height, meshHeightMm(o.positions, o.scale));
  }
  if (vol <= 0) {
    return { volumeCm3: 0, filamentGrams: 0, approxMinutes: 0, note: 'No mesh volume.' };
  }

  const density =
    typeof settings.filament_density === 'number'
      ? settings.filament_density
      : parseFloat(String(settings.filament_density ?? 1.26));
  const infill =
    typeof settings.sparse_infill_density === 'number'
      ? settings.sparse_infill_density
      : parseFloat(String(settings.sparse_infill_density ?? 15));
  const walls = Number(settings.wall_loops ?? 2);
  const top = Number(settings.top_shell_layers ?? 5);
  const bottom = Number(settings.bottom_shell_layers ?? 3);
  const layerH = Number(settings.layer_height ?? 0.2) || 0.2;
  const speed = Number(settings.inner_wall_speed ?? 300) || 300;
  const lineW = Number(settings.line_width ?? settings.outer_wall_line_width ?? 0.42) || 0.42;

  const charLen = Math.max(8, Math.cbrt(vol));
  const h = Math.max(height, layerH);
  const wallShell = (5.0 * walls * lineW) / charLen;
  const tbShell = ((top + bottom) * layerH) / h;
  const shellFrac = Math.min(0.85, wallShell + tbShell);
  const fillFactor = Math.min(0.95, shellFrac + (1 - shellFrac) * (infill / 100));

  const volumeCm3 = (vol / 1000) * fillFactor;
  const filamentGrams = volumeCm3 * density;

  // Extrusion cross-section ≈ line width × layer height (matches Orca time better than nozzle πr²)
  const pathMm = (vol * fillFactor) / Math.max(1e-6, lineW * layerH);
  const layers = Math.max(1, Math.ceil(h / layerH));
  const approxMinutes = Math.max(
    1,
    Math.round(pathMm / speed / 60 + layers * 0.15 + objects.length * 1.5 + 2)
  );

  return {
    volumeCm3: Math.round(volumeCm3 * 100) / 100,
    filamentGrams: Math.round(filamentGrams * 10) / 10,
    approxMinutes,
    note: 'Rough estimate (calibrated vs Orca on 20 mm cube + 90×78×66 box) — not a true slice preview.',
  };
}
